import PropTypes from "prop-types";
import { useRef } from "react";
// @mui
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
// components
import Iconify from "src/components/iconify";
//
import { cellClassName } from "./grid-rows";

export const COLUMN_DRAG_TYPE = "application/x-consuman-grid-column";

function headerStyle(viewColumn) {
  if (viewColumn.fixed === "left") return { left: viewColumn.offset };
  if (viewColumn.fixed === "right") return { right: viewColumn.offset };

  return undefined;
}

function clearDropMarks(element) {
  element
    ?.closest("tr")
    ?.querySelectorAll(".cg-drop-before, .cg-drop-after")
    .forEach(cell => {
      cell.classList.remove("cg-drop-before", "cg-drop-after");
    });
}

function HeaderCell({ viewColumn, ctx }) {
  const { column } = viewColumn;
  const { sortOrder, sortIndex } = viewColumn;
  const cellRef = useRef(null);
  const sortable = ctx.sortingMode !== "none" && column.allowSorting;
  const filterActive = ctx.isColumnFiltered(column);
  const showHeaderFilter = ctx.headerFilterVisible && column.allowHeaderFiltering;
  const draggable = ctx.canDragColumn(column);

  const handleClick = event => {
    if (!sortable || event.defaultPrevented) return;
    if (event.target.closest(".cg-header-action, .cg-resize-handle, .cg-header-custom")) return;

    ctx.toggleSort(column, event);
  };

  const menuAvailable = ctx.hasColumnMenu(column);

  const handleContextMenu = event => {
    if (!menuAvailable) return;

    event.preventDefault();
    ctx.openColumnMenu(column, event.currentTarget, { left: event.clientX, top: event.clientY });
  };

  const handleDragStart = event => {
    event.dataTransfer.setData(COLUMN_DRAG_TYPE, column.id);
    event.dataTransfer.effectAllowed = "move";
    ctx.setDraggedColumn(column.id);
    event.currentTarget.classList.add("cg-dragging");
  };

  const handleDragEnd = event => {
    event.currentTarget.classList.remove("cg-dragging");
    clearDropMarks(event.currentTarget);
    ctx.setDraggedColumn(null);
  };

  const handleDragOver = event => {
    const dragged = ctx.getDraggedColumn();

    if (!dragged || dragged === column.id || !ctx.allowColumnReordering) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    const rect = event.currentTarget.getBoundingClientRect();
    const before = event.clientX < rect.left + rect.width / 2;

    clearDropMarks(event.currentTarget);
    event.currentTarget.classList.add(before ? "cg-drop-before" : "cg-drop-after");
  };

  const handleDrop = event => {
    const dragged = event.dataTransfer.getData(COLUMN_DRAG_TYPE) || ctx.getDraggedColumn();

    clearDropMarks(event.currentTarget);

    if (!dragged || dragged === column.id) return;

    event.preventDefault();

    const rect = event.currentTarget.getBoundingClientRect();
    const before = event.clientX < rect.left + rect.width / 2;

    ctx.dropColumn(dragged, column.id, before);
  };

  const caption = column.headerCellRender ? (
    <Box
      className="cg-header-custom"
      sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: "1 1 auto" }}
    >
      {column.headerCellRender({ column, columnIndex: viewColumn.index, component: ctx.instance })}
    </Box>
  ) : (
    <span className="cg-header-caption">{column.caption}</span>
  );

  const prefix = ctx.headerPrefix?.(viewColumn);

  const content = (
    <>
      {prefix ? <span className="cg-header-action cg-header-prefix">{prefix}</span> : null}
      {caption}
      {sortOrder ? (
        <Box
          component="span"
          className="cg-sort-icon"
          sx={{ display: "inline-flex", alignItems: "center" }}
        >
          <Iconify
            icon={sortOrder === "asc" ? "eva:arrow-upward-fill" : "eva:arrow-downward-fill"}
            width={16}
          />
          {ctx.multipleSort && ctx.sortCount > 1 ? (
            <Box
              component="span"
              sx={{ typography: "caption", fontSize: 10, lineHeight: 1 }}
            >
              {sortIndex + 1}
            </Box>
          ) : null}
        </Box>
      ) : null}
      {showHeaderFilter && filterActive ? (
        <Box
          component="span"
          className="cg-filter-indicator"
          sx={{ display: "inline-flex", color: "primary.main", flexShrink: 0 }}
        >
          <Iconify
            icon="mdi:filter"
            width={14}
          />
        </Box>
      ) : null}
    </>
  );

  return (
    <th
      ref={cellRef}
      className={cellClassName(viewColumn, sortable ? "cg-sortable" : undefined)}
      style={headerStyle(viewColumn)}
      data-align={column.alignment}
      data-col-index={viewColumn.index}
      draggable={draggable}
      onContextMenu={handleContextMenu}
      onDragStart={draggable ? handleDragStart : undefined}
      onDragEnd={draggable ? handleDragEnd : undefined}
      onDragOver={handleDragOver}
      onDragLeave={event => event.currentTarget.classList.remove("cg-drop-before", "cg-drop-after")}
      onDrop={handleDrop}
      aria-sort={sortOrder ? (sortOrder === "asc" ? "ascending" : "descending") : undefined}
      title={column.headerCellRender ? undefined : column.caption}
    >
      {sortable ? (
        <div
          className="cg-header-cell"
          data-align={column.alignment}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              ctx.toggleSort(column, event);
            }
          }}
        >
          {content}
        </div>
      ) : (
        <div
          className="cg-header-cell"
          data-align={column.alignment}
        >
          {content}
        </div>
      )}
      {showHeaderFilter || menuAvailable ? (
        <div className="cg-header-actions">
          {showHeaderFilter ? (
            <Tooltip title={ctx.t("gridFilterOptions")}>
              <IconButton
                size="small"
                className={`cg-header-action${filterActive ? " cg-active" : ""}`}
                color={filterActive ? "primary" : "default"}
                onClick={event => {
                  event.stopPropagation();
                  ctx.openHeaderFilter(column, event.currentTarget);
                }}
                sx={{ p: 0.25 }}
                aria-label={ctx.t("gridFilterOptions")}
              >
                <Iconify
                  icon={filterActive ? "mdi:filter" : "mdi:filter-outline"}
                  width={16}
                />
              </IconButton>
            </Tooltip>
          ) : null}
          {menuAvailable ? (
            <IconButton
              size="small"
              className="cg-header-action"
              onClick={event => {
                event.stopPropagation();
                ctx.openColumnMenu(column, event.currentTarget);
              }}
              sx={{ p: 0.25 }}
              aria-label={ctx.t("gridColumnOptions")}
            >
              <Iconify
                icon="eva:more-vertical-fill"
                width={16}
              />
            </IconButton>
          ) : null}
        </div>
      ) : null}
      {ctx.allowColumnResizing && column.allowResizing ? (
        <span
          className="cg-resize-handle"
          role="separator"
          aria-orientation="vertical"
          onPointerDown={event => ctx.startResize(event, column, cellRef.current)}
          onDoubleClick={event => {
            event.stopPropagation();
            ctx.autoFitColumn(column);
          }}
        />
      ) : null}
    </th>
  );
}

HeaderCell.propTypes = {
  viewColumn: PropTypes.object,
  ctx: PropTypes.object,
};

function CommandHeaderCell({ viewColumn, ctx }) {
  let content = null;

  if (viewColumn.kind === "select") {
    if (viewColumn.column?.headerCellRender)
      content = viewColumn.column.headerCellRender({
        column: viewColumn.column,
        columnIndex: viewColumn.index,
        component: ctx.instance,
      });
    else if (ctx.allowSelectAll) {
      const { all, some } = ctx.selectAllState();

      content = (
        <Checkbox
          size="small"
          checked={all}
          indeterminate={!all && some}
          onChange={event => ctx.selectAllLoaded(event.target.checked)}
          inputProps={{ "aria-label": ctx.t("gridSelectAll") }}
          sx={{ p: 0.5 }}
        />
      );
    }
  }

  return (
    <th
      className={cellClassName(viewColumn)}
      style={headerStyle(viewColumn)}
      data-col-index={viewColumn.index}
    >
      {content}
    </th>
  );
}

CommandHeaderCell.propTypes = {
  viewColumn: PropTypes.object,
  ctx: PropTypes.object,
};

export default function HeaderRow({ viewColumns, ctx }) {
  return (
    <tr className="cg-header-row">
      {viewColumns.map(viewColumn =>
        viewColumn.kind === "data" ? (
          <HeaderCell
            key={viewColumn.key}
            viewColumn={viewColumn}
            ctx={ctx}
          />
        ) : (
          <CommandHeaderCell
            key={viewColumn.key}
            viewColumn={viewColumn}
            ctx={ctx}
          />
        )
      )}
    </tr>
  );
}

HeaderRow.propTypes = {
  viewColumns: PropTypes.array,
  ctx: PropTypes.object,
};
